import {
  AppConstants,
  conceptIDs,
  JUKServices,
  MLY_IDS,
  RegexConstants,
  stateAbbreviations,
  UPSELL_VARIABLES,
} from "../constants/constant";
import {
  DASH_FOR_PRICE_QUOTE_FIELDS,
  landingPageOfferSource,
} from "../feature/quotation/Constants/constants";
import { SelectedServices } from "../Interface/quotation/form";
import {
  cityState,
  FormValues,
  SchedulerConfig,
} from "../Interface/scheduler/form";
import apiConfig from "../service/apiConfig";
import isInboundFromNbly from "./checkInBoundFromNbly";

/**
 * Formats a single address into a readable string.
 * @param {Object} address - The address object.
 * @returns {string} The formatted address.
 */
const formatAddress = (address) => {
  if (address) {
    const line2 = address.addressLine2 ? `.${address.addressLine2}` : "";
    return `${address.addressLine1 ?? ""}${line2}, ${address.city ?? ""}, ${address.state ?? ""}, ${address.zip ?? ""}`;
  }
  return "No address provided";
};

export const removeConsecutiveCommas = (string): string => {
  return string.replace(/,\s*,/g, ",");
};

export const getStateAbbreviation = (
  stateName: string | undefined,
): string | undefined => {
  if (stateName?.length === 2) {
    return stateName;
  } else {
    return stateName ? (stateAbbreviations[stateName] ?? stateName) : "";
  }
};

/**
 * Formats an array of addresses into a list of formatted address strings.
 * @param {Array} addresses - An array of address objects.
 * @returns {Array} An array of formatted address strings.
 */
const formatAddresses = (addresses) => {
  if (Array.isArray(addresses) && addresses.length > 0) {
    return addresses.map((address) => formatAddress(address));
  }
  return ["No addresses provided"];
};

const getFeeWrapperBackgroundColor = (
  conceptCode: string | undefined,
): string => {
  const brandsWithBlueWrapper = ["GUY", "MRR", "PDS", "WDG"];
  const isNotMRACheckTheming =
    conceptCode !== "MRA" ? "primary.light" : "background.default";
  return brandsWithBlueWrapper.includes(conceptCode || "")
    ? "FeeWrapper.lightBlueWrapperBackground"
    : isNotMRACheckTheming;
};

export const sessionStorageUtils = {
  removeItem: (key: string) => sessionStorage.removeItem(key),
  getItem: (key: string): string | null => sessionStorage.getItem(key),
  setItem: <T>(key: string, value: T): void => {
    const serializedValue =
      typeof value === "string" ? value : JSON.stringify(value);
    try {
      const size = new Blob([serializedValue]).size;
      if (size > 5 * 1024 * 1024) {
        console.warn(`Item too large for sessionStorage: ${size} bytes`);
        return;
      }
      sessionStorage.setItem(key, serializedValue);
    } catch (e) {
      console.error("Error storing item in sessionStorage", e);
    }
  },
};
export const formateZipCode = (zipCode: string | null | undefined): string => {
  if (!zipCode) return "";
  const trimmedZipCode = zipCode.trim();
  if (RegexConstants.US_ZIP_CODE_REG.test(trimmedZipCode)) {
    return trimmedZipCode;
  }
  const upperCaseZipCode = trimmedZipCode.toUpperCase();
  if (upperCaseZipCode.includes(" ")) {
    return upperCaseZipCode;
  }

  return `${upperCaseZipCode.substring(0, 3)} ${upperCaseZipCode.substring(3)}`;
};

export const landingPageOfferSourceValidation = () => {
  try {
    const offerJson = sessionStorageUtils.getItem("offerDetails");
    if (!offerJson) return false;
    const validOffer = JSON.parse(offerJson);
    return validOffer?.source === landingPageOfferSource;
  } catch (error) {
    console.error("Error parsing offer details from sessionStorage", error);
    return false;
  }
};

function findPropertyById(config: SchedulerConfig, id: string) {
  for (const errorGroup of config.errors) {
    for (const { elements } of errorGroup) {
      const element = elements.find((el) => el.id === id);
      if (element) {
        return element.label || element.src || element.image || null;
      }
    }
  }
  return null;
}

const findLabelById = (data, targetId) => {
  const stack = [data];
  while (stack.length > 0) {
    const currentItem = stack.pop();
    if (Array.isArray(currentItem)) {
      for (let i = currentItem.length - 1; i >= 0; i--) {
        stack.push(currentItem[i]);
      }
      continue;
    }
    if (typeof currentItem === "object" && currentItem !== null) {
      if (currentItem.id === targetId && currentItem.label) {
        return currentItem.label;
      }
      Object.keys(currentItem).forEach((key) => {
        if (Object.hasOwn(currentItem, key)) {
          stack.push(currentItem[key]);
        }
      });
    }
  }
  return null;
};

function isPostalCodeMatch(
  item,
  zipCodeFromFormValues: string,
  noAddressMsg: string,
): boolean {
  const {
    houseNumber = "",
    street = "",
    building = "",
    postalCode: rawPostalCode = "",
  } = item.address;

  // Construct address1
  const address1 =
    [houseNumber, street, building].filter(Boolean).join(" ") || noAddressMsg;
  item.isAddressCollected = address1;

  // Normalize postalCode (remove spaces, lowercase)
  let postalCode = rawPostalCode.replace(/\s/g, "").toLowerCase();
  const zipCodeNormalized = zipCodeFromFormValues
    .replace(/\s/g, "")
    .toLowerCase();

  // Handle postal codes with dashes

  if (!postalCode) {
    return;
  }
  if (postalCode.includes("-")) {
    postalCode = postalCode.split("-")[0]; // Take only the first part before '-'
    item.address.postalCode = postalCode;

    // Update label if needed
    const labelParts = item.address.label.split(rawPostalCode);
    if (labelParts.length > 1) {
      item.address.label =
        labelParts[0] + postalCode + "," + labelParts[1].split(",")[1];
    }
  }

  // Check if the postal code is a Canadian postal code (format: A1A 1A1)
  const isCanadianZip = RegexConstants.CA_ZIP_CODE_REG.test(zipCodeNormalized);

  if (isCanadianZip) {
    const firstThree = zipCodeNormalized.substring(0, 3);
    const firstSix = zipCodeNormalized.substring(0, 6);

    return firstThree === postalCode || firstSix === postalCode;
  }

  // Default comparison for other zip codes
  return zipCodeNormalized.includes(postalCode);
}
const debounce = (func, delay) => {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
  debounced.cancel = () => {
    clearTimeout(timer);
  };
  return debounced;
};

function generateTechAvailablityAPIUrl(
  formValues,
  formConfig,
  locationId,
  startDate,
  days,
  wdgServiceAccordionNonPriciableSkuId,
) {
  const {
    service,
    zipcode,
    selectedPriceQuoteSkuIds,
    franchiseId,
    licenseNumber,
    address,
    address1,
    durationInMin = 0,
    serviceRequestImage,
    serviceName,
  } = formValues;

  const { conceptid } = formConfig;
  let isEstimate = true;
  let isDumpster = false;
  let baseUrl = `${apiConfig.techAvailabiltyURL}?apikey=${apiConfig.API_KEY}&WebLocationId=${locationId}&DateStart=${startDate}&NumberOfDays=${days}&PostalCode=${zipcode}`;
  if (
    conceptid === conceptIDs.MLY_CONCEPT_ID &&
    !formValues[MLY_IDS.FREQUENCY]?.includes("recurring")
  ) {
    isEstimate = false;
  }

  if (conceptid === conceptIDs.WDG_CONCEPT_ID) {
    isEstimate = false;
    const finalAddress = address1 || address;
    const firstWdgSkuId = getFirstValueFromObj(selectedPriceQuoteSkuIds);
    if (firstWdgSkuId) {
      baseUrl += `&ServiceSkuId=${firstWdgSkuId}`;
    } else if (wdgServiceAccordionNonPriciableSkuId) {
      baseUrl += `&ServiceSkuId=${wdgServiceAccordionNonPriciableSkuId}`;
    }
    let durationInMinParmStr = "";
    if (durationInMin && durationInMin > 0) {
      durationInMinParmStr = `&DurationInMin=${durationInMin}`;
    }
    baseUrl += `&LicenseNumber=${licenseNumber}&FranchiseId=${franchiseId}&address=${finalAddress}${durationInMinParmStr}`;
  }
  if (conceptid === conceptIDs.JUK_CONCEPT_ID) {
    if (serviceName === JUKServices.DUMPSTER_RENTAL) {
      isDumpster = true;
    }
    baseUrl += `&isDumpster=${isDumpster}`;
  }

  if (formValues.manufacturerWarranty === "no") {
    baseUrl += `&ServiceSkuId=${serviceRequestImage[0].serviceid}`;
  }

  baseUrl += `&IsEstimate=${isEstimate}`;
  return service ? `${baseUrl}&ServiceSkuId=${service}` : baseUrl;
}

function getFirstValueFromObj(obj) {
  if (
    !obj ||
    typeof obj !== "object" ||
    Array.isArray(obj) ||
    Object.keys(obj).length === 0
  ) {
    return null;
  }

  const servicePriorityOrder = [
    "windowCleaning",
    "pressure-washing",
    "gutter-cleaning",
    "holiday-lighting",
    "window-tinting",
  ];
  for (const service of servicePriorityOrder) {
    if (obj.hasOwnProperty(service)) {
      return obj[service];
    }
  }
  const firstKey = Object.keys(obj)[0];
  return obj[firstKey];
}

function formatPrice(price) {
  if (price === DASH_FOR_PRICE_QUOTE_FIELDS) {
    return price;
  } else if (isNaN(price) || price === null || price === undefined) {
    return "0.00";
  }

  const formattedPrice = Number(price)?.toFixed(2);
  // Add thousands comma
  return Number(formattedPrice).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function addDollarToPrice(price) {
  if (price === DASH_FOR_PRICE_QUOTE_FIELDS) {
    return "";
  } else {
    return "$";
  }
}

const preventInputNonNumeric = (
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
) => {
  if (
    !/^\d$/.test(e.key) && // Only allow numbers
    e.key !== "Backspace" &&
    e.key !== "Delete" &&
    e.key !== "Tab" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight"
  ) {
    e.preventDefault();
  }
};

function mergeArrayObjById(
  arr1: SelectedServices[],
  arr2: SelectedServices[],
): SelectedServices[] {
  const mergedMap = new Map(arr1.map((item) => [item.id, item]));
  arr2.forEach((item) => mergedMap.set(item.id, item));
  return Array.from(mergedMap.values());
}

//Use this to only check objects with arrays/objects. Not the ones with functions nested
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!Object.hasOwn(b as Record<string, unknown>, key)) return false;

    const valA = (a as Record<string, unknown>)[key];
    const valB = (b as Record<string, unknown>)[key];

    if (valA === valB) continue;

    if (
      typeof valA !== "object" ||
      valA === null ||
      typeof valB !== "object" ||
      valB === null
    ) {
      return false;
    }

    if (!deepEqual(valA, valB)) return false;
  }

  return true;
}

const getRandomInt = (max: number) => {
  if (max > 0) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] % max;
  }
  return 0;
};

const skipMLYCalendar = (formValues: FormValues) => {
  return (
    (formValues.isCalendarDisabled &&
      formValues?.[MLY_IDS.FREQUENCY] === MLY_IDS.ONE_TIME_CLEAN) ||
    (!formValues.onlineScheduling &&
      formValues?.[MLY_IDS.FREQUENCY] === MLY_IDS.RECURRING_CLEAN) ||
    formValues.servicetype === MLY_IDS.COMMERCIAL
  );
};

export const checkGetCachedWrapperFunc = () => {
  return (
    window?.GetCachedResponse && typeof window.GetCachedResponse === "function"
  );
};

/**
 * Retry function with exponential backoff for checking window API availability
 * @param checkFunction - Function that returns true when the condition is met
 * @param onSuccess - Callback to execute when condition is met
 * @param onTimeout - Callback to execute when max wait time is reached
 * @param options - Configuration options
 */
export const retryWithExponentialBackoff = (
  checkFunction: () => boolean,
  onSuccess: () => void,
  onTimeout: () => void = () => {},
): (() => void) => {
  const maxWaitTime = 10000; // 10 seconds default
  const maxInterval = 2000; // 2 seconds max interval
  const initialInterval = 100; // 100ms initial interval

  let currentInterval = initialInterval;
  let totalWaitTime = 0;
  let timeoutId: NodeJS.Timeout;

  const scheduleNextCheck = () => {
    if (totalWaitTime >= maxWaitTime) {
      onTimeout?.();
    }

    timeoutId = setTimeout(() => {
      if (checkFunction()) {
        onSuccess();
        return;
      }

      totalWaitTime += currentInterval;
      currentInterval = Math.min(currentInterval * 2, maxInterval);

      scheduleNextCheck();
    }, currentInterval);
  };

  // Check immediately
  if (checkFunction()) {
    onSuccess();
  } else {
    scheduleNextCheck();
  }

  // Return cleanup function
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};

/**
 * Dynamically calculates the total header height based on visibility and fixed state
 * Handles two scenarios:
 * 1. Header not fixed: Both nbly-main.first-head and second-head.form-header are visible
 * 2. Header fixed: Only second-head.form-header with 'fixed' class is visible
 * @returns {number} Total height of visible headers in pixels
 */
export const getHeaderHeight = (): number => {
  const firstHead = document.querySelector(".nbly-main.first-head");
  const secondHead = document.querySelector(".second-head.form-header");

  let totalHeight = 0;

  // If second head is fixed, only count its height
  if (secondHead?.classList.contains("fixed")) {
    totalHeight = secondHead.getBoundingClientRect().height;
  } else {
    // If not fixed, both headers are visible - count both
    // Check if first header is visible (not hidden and has actual height)
    if (firstHead && !firstHead.classList.contains("hidden")) {
      const firstHeadRect = firstHead.getBoundingClientRect();
      if (firstHeadRect.height > 0) {
        totalHeight += firstHeadRect.height;
      }
    }

    // Always check second header if it exists and has height
    if (secondHead) {
      const secondHeadRect = secondHead.getBoundingClientRect();
      if (secondHeadRect.height > 0) {
        totalHeight += secondHeadRect.height;
      }
    }
  }

  return totalHeight;
};

function capitalizeFirstLetter(str) {
  if (typeof str !== "string" || str.length === 0) {
    return str; // Handle non-string input or empty string
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const getCPEstimateFlag = (schedulerConfig: SchedulerConfig): boolean => {
  const calendarStep = schedulerConfig?.steps?.find(
    (step) => step.steptype === AppConstants.REQUEST_TIME_TYPE,
  );
  return calendarStep?.["one-time-clean"]?.hideEstimateSection || false;
};

const isInvalidCityState = (city: cityState, state: cityState): boolean => {
  if (!city || !state || !city.trim() || !state.trim()) {
    return true;
  }
  if (
    city?.trim()?.toLowerCase() === AppConstants?.NO_CITY_FOUND?.toLowerCase()
  ) {
    return true;
  }
  if (
    state?.trim()?.toLowerCase() === AppConstants?.NO_STATE_FOUND?.toLowerCase()
  ) {
    return true;
  }
  return false;
};

const renderUpsellValue = async (conceptCode: string) => {
  let url = apiConfig?.upsellApi;
  if (!apiConfig?.upsellApi) {
    console.error("Upsell API endpoint is not configured");
    return;
  }
  if (isInboundFromNbly) {
    url = `${apiConfig.upsellApi}?leadconcept=${conceptCode}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response;
};

const extractUpsellValue = (serviceDescription) => {
  if (serviceDescription) {
    const upsellService = serviceDescription
      .split(UPSELL_VARIABLES.UPSELL_NOTES_TEXT)[1]
      ?.replace(/^:\s*/, "")
      .trim();
    return upsellService;
  }
  return "";
};
export {
  formatAddress,
  formatAddresses,
  getFeeWrapperBackgroundColor,
  findPropertyById,
  findLabelById,
  isPostalCodeMatch,
  debounce,
  generateTechAvailablityAPIUrl,
  getFirstValueFromObj,
  formatPrice,
  preventInputNonNumeric,
  mergeArrayObjById,
  addDollarToPrice,
  deepEqual,
  getRandomInt,
  skipMLYCalendar,
  capitalizeFirstLetter,
  getCPEstimateFlag,
  isInvalidCityState,
  renderUpsellValue,
  extractUpsellValue,
};
